import { MutationTree } from "vuex";

import { addItemManipulacaoContext, updateManipulacao } from "@/components/parent/manipulacaoNew/manipulacaoUtils";
import SituacoesManipulacaoOrdem from "@/models/enum/situacoesManipulacaoOrdem";
import TiposCalculoManipulacao from "@/models/enum/tiposCalculoManipulacao";
import TiposItemManipulacao from "@/models/enum/tiposItemManipulacao";
import ManipulacaoOrdemItemModel from "@/models/manipulacaoOrdemItemModel";
import ManipulacaoOrdemModel from "@/models/manipulacaoOrdemModel";
import IndicesConversoesUnidadeMedidasService from "@/services/indicesConversoesUnidadeMedidaService";
import { isEqual } from "@/utils/common/copyObject";

import {
    ActionItemType,
    ItemTableType,
    ManipulacaoContextNewState,
    ManipulacaoContextUUIDNew,
    ResetManipulacaoType,
    UpdateItemType,
} from "./types";

const indicesConversoesUnidadeMedidasService = new IndicesConversoesUnidadeMedidasService();

const mutations: MutationTree<Map<string, ManipulacaoContextNewState>> = {
    ADD_NEW_CONTEXT_NEW(store, id: string) {
        const newState = new ManipulacaoContextNewState();
        newState.indicesConversoesUnidadeMedidasService = indicesConversoesUnidadeMedidasService;
        store.set(id, newState);
    },
    RESET_MANIPULACAO_NEW(store, data: ManipulacaoContextUUIDNew<ResetManipulacaoType>) {
        const newState = new ManipulacaoContextNewState();
        newState.indicesConversoesUnidadeMedidasService = indicesConversoesUnidadeMedidasService;
        newState.isFranquiaFilial = false;
        newState.isVendaReplicada = data.data.isVendaReplicada;
        newState.isItemFromVenda = data.data.isItemFromVenda;
        newState.isTransferencia = data.data.isTransferencia;
        store.set(data.uuid, newState);
    },
    UPDATE_MANIPULACAO_NEW(store, { uuid, data }: ManipulacaoContextUUIDNew<ManipulacaoOrdemModel>) {
        let state = store.get(uuid);

        if (isEqual(state.manipulacao, data)) {
            return;
        }

        state.manipulacao = data;

        if (state.ativos.length == 0) {
            state.ativos = state.manipulacao.itens.filter(
                p =>
                    (p.tipoItem == TiposItemManipulacao.MateriaPrima ||
                        p.tipoItem == TiposItemManipulacao.FormulaPadrao) &&
                    p.produtoPai == null,
            );
        }

        if (state.manipulacao.itens.some(p => p.tipoItem == TiposItemManipulacao.FormulaPadrao)) {
            if (state.desmembrados.length == 0 && state.manipulacao.itens.some(p => p.produtoPai != null)) {
                state.desmembrados = state.manipulacao.itens.filter(p => p.produtoPai != null);
            }
        } else {
            state.desmembrados = [];
        }

        if (state.embalagens.length == 0) {
            state.embalagens = state.manipulacao.itens.filter(
                p => p.tipoItem == TiposItemManipulacao.Embalagem || p.tipoItem == TiposItemManipulacao.KitEmbalagem,
            );
        }

        if (state.capsulas.length == 0) {
            state.capsulas = state.manipulacao.itens.filter(p => p.tipoItem == TiposItemManipulacao.Capsula);
        }

        if (
            state.manipulacao.situacao == SituacoesManipulacaoOrdem.Cancelado ||
            state.manipulacao.situacao == SituacoesManipulacaoOrdem.Concluido
        ) {
            state.isDesabilitado = true;
        }

        if (state.ativos.length > 0 && !state.ativos.some(p => p.ativoPrincipal)) {
            state.ativos[0].ativoPrincipal = true;
        }

        state = updateManipulacao(state);
        store.set(uuid, state);
    },
    CONSOLIDAR_NEW(store, { uuid, data }: ManipulacaoContextUUIDNew<ManipulacaoOrdemModel>) {
        let state = store.get(uuid);

        if (isEqual(state.manipulacao, data)) {
            return;
        }

        state.manipulacao = data;
        state.ativos = state.manipulacao.itens.filter(
            p =>
                (p.tipoItem == TiposItemManipulacao.MateriaPrima || p.tipoItem == TiposItemManipulacao.FormulaPadrao) &&
                p.produtoPai == null,
        );
        state.desmembrados = state.manipulacao.itens.filter(p => p.produtoPai != null);
        state.embalagens = state.manipulacao.itens.filter(
            p => p.tipoItem == TiposItemManipulacao.Embalagem || p.tipoItem == TiposItemManipulacao.KitEmbalagem,
        );

        if (state.embalagens[0]) {
            state.manipulacao.kitEmbalagemId = state.embalagens[0].kitEmbalagemId;
        }

        state.capsulas = state.manipulacao.itens.filter(p => p.tipoItem == TiposItemManipulacao.Capsula);
        state = updateManipulacao(state);
        store.set(uuid, state);
    },
    UPDATE_MANIPULACAO_FROM_DESMEMBRADOS_NEW(store, { uuid, data }: ManipulacaoContextUUIDNew<ManipulacaoOrdemModel>) {
        let state = store.get(uuid);

        if (isEqual(state.manipulacao, data)) {
            return;
        }

        state.manipulacao = data;

        state.ativos = [];
        state.desmembrados = [];

        if (state.ativos.length == 0) {
            state.ativos = state.manipulacao.itens.filter(
                p =>
                    (p.tipoItem == TiposItemManipulacao.MateriaPrima ||
                        p.tipoItem == TiposItemManipulacao.FormulaPadrao) &&
                    p.produtoPai == null,
            );
        }

        if (state.manipulacao.itens.some(p => p.tipoItem == TiposItemManipulacao.FormulaPadrao)) {
            if (state.desmembrados.length == 0 && state.manipulacao.itens.some(p => p.produtoPai != null)) {
                state.desmembrados = state.manipulacao.itens.filter(p => p.produtoPai != null);
            }
        }

        state = updateManipulacao(state);
        store.set(uuid, state);
    },
    UPDATE_MANIPULACAO_KIT_EMBALAGEM_ID_NEW(store, { uuid, data }: ManipulacaoContextUUIDNew<number>) {
        const state = store.get(uuid);
        state.manipulacao.kitEmbalagemId = data;
        store.set(uuid, state);
    },
    SET_UPLOADING_EDITING_MODEL_NEW(store, { uuid, data }: ManipulacaoContextUUIDNew<boolean>) {
        const state = store.get(uuid);
        state.uploadingEditingModel = data;
        store.set(uuid, state);
    },
    SET_UPLOADING_PREVENDA_NEW(store, { uuid, data }: ManipulacaoContextUUIDNew<boolean>) {
        const state = store.get(uuid);
        state.uplodingPrevenda = data;
        store.set(uuid, state);
    },
    SET_IS_CAPSULA_NEW(store, { uuid, data }: ManipulacaoContextUUIDNew<boolean>) {
        const state = store.get(uuid);
        state.isCapsula = data;
        store.set(uuid, state);
    },
    SET_IS_CONSOLIDANDO_NEW(store, { uuid, data }: ManipulacaoContextUUIDNew<boolean>) {
        const state = store.get(uuid);
        state.isConsolidando = data;
        store.set(uuid, state);
    },
    SET_IS_DESABILITADO_NEW(store, { uuid, data }: ManipulacaoContextUUIDNew<boolean>) {
        const state = store.get(uuid);
        state.isDesabilitado = data;
        store.set(uuid, state);
    },
    ADD_ITEM_MANIPULACAO_NEW(store, { uuid, data }: ManipulacaoContextUUIDNew<ActionItemType>) {
        let state = store.get(uuid);
        const { item, type } = data;

        if (item) {
            if (!item.valor) {
                item.valor = 0.01;
            }

            if (item.produtoListaControladoId) {
                state.temProdutoListacontrolado = true;
            }

            switch (type) {
                case ItemTableType.ATIVO:
                    state.ativos = addItemManipulacaoContext(
                        state.ativos,
                        item,
                        TiposItemManipulacao.MateriaPrima,
                        data,
                    );
                    break;
                case ItemTableType.EMBALAGEM:
                    state.embalagens = addItemManipulacaoContext(
                        state.embalagens,
                        item,
                        TiposItemManipulacao.Embalagem,
                        data,
                    );
                    break;
                case ItemTableType.CAPSULA:
                    state.capsulas = addItemManipulacaoContext(
                        state.capsulas,
                        item,
                        TiposItemManipulacao.Capsula,
                        data,
                    );
                    break;
            }
            state = updateManipulacao(state);
        } else {
            switch (type) {
                case ItemTableType.ATIVO:
                    state.ativos.push(item);
                    break;
                case ItemTableType.EMBALAGEM:
                    state.embalagens.push(item);
                    break;
                case ItemTableType.CAPSULA:
                    state.capsulas.push(item);
                    break;
            }
        }
        store.set(uuid, state);
    },
    REMOVE_EMBALAGENS_BY_KIT_ID_NEW(store, { uuid, data }: ManipulacaoContextUUIDNew<number>) {
        const state = store.get(uuid);
        state.embalagens = state.embalagens.filter(p => p.kitEmbalagemId != data);
        store.set(uuid, state);
    },
    UPDATE_ITENS_KIT_EMBALAGEM_NEW(store, { uuid, data }: ManipulacaoContextUUIDNew<ManipulacaoOrdemItemModel[]>) {
        const state = store.get(uuid);
        state.embalagens = data;
        store.set(uuid, state);
    },
    REMOVE_ALL_ITENS_BY_TYPE_NEW(store, { uuid, data }: ManipulacaoContextUUIDNew<ItemTableType>) {
        const state = store.get(uuid);
        switch (data) {
            case ItemTableType.ATIVO:
                state.ativos = [];
                state.desmembrados = [];
                break;
            case ItemTableType.EMBALAGEM:
                state.embalagens = [];
                break;
            case ItemTableType.CAPSULA:
                state.capsulas = [];
                break;
        }
        store.set(uuid, state);
    },
    REMOVE_ITEM_MANIPULACAO_NEW(store, { uuid, data }: ManipulacaoContextUUIDNew<ActionItemType>) {
        let state = store.get(uuid);
        switch (data.type) {
            case ItemTableType.ATIVO:
                const ativo = state.ativos[data.index];
                if (ativo.produtoPai == null && ativo.formulaPadraoId != null) {
                    state.desmembrados = state.desmembrados.filter(
                        p => p.manipulacaoOrdemItemProdutoPaiId !== ativo.produtoId,
                    );
                }
                state.ativos.splice(data.index, 1);
                break;
            case ItemTableType.EMBALAGEM:
                state.embalagens.splice(data.index, 1);
                break;
            case ItemTableType.CAPSULA:
                state.capsulas.splice(data.index, 1);
                break;
        }
        state = updateManipulacao(state);
        store.set(uuid, state);
    },
    CALCULAR_QSP_NEW(store, { uuid, data }: ManipulacaoContextUUIDNew<ManipulacaoOrdemItemModel[]>) {
        let state = store.get(uuid);
        state.ativos = data;
        state = updateManipulacao(state);
        store.set(uuid, state);
    },
    REMOVE_ATIVOS_ASSOCIADOS_NEW(store, { uuid }: ManipulacaoContextUUIDNew<null>) {
        const state = store.get(uuid);
        state.ativos = state.ativos.filter(p => p.produtoAssociadoPaiId == null || p.formulaPadraoId > 0);
        state.manipulacao.itens = state.manipulacao.itens.filter(
            p => p.produtoAssociadoPaiId == null || p.formulaPadraoId > 0,
        );
        store.set(uuid, state);
    },
    REMOVE_DESMEMBRADOS_NEW(store, { uuid }: ManipulacaoContextUUIDNew<null>) {
        const state = store.get(uuid);
        state.desmembrados = [];
        state.manipulacao.itens = state.manipulacao.itens.filter(p => !p.produtoPai);
        store.set(uuid, state);
    },
    REMOVE_ATIVOS_ASSOCIADOS_FORMA_FARMACEUTICA_NEW(store, { uuid }: ManipulacaoContextUUIDNew<null>) {
        const state = store.get(uuid);
        state.ativos = state.ativos.filter(p => p.formaFarmaceuticaId == null);
        state.manipulacao.itens = state.manipulacao.itens.filter(p => p.formaFarmaceuticaId == null);
        store.set(uuid, state);
    },
    REPLICA_DOSE_ATIVOS_NEW(
        store,
        { uuid, data }: ManipulacaoContextUUIDNew<{ dose: number; isTrocaManual?: boolean }>,
    ) {
        const state = store.get(uuid);

        state.ativos
            .filter(p => p.tipoCalculo == TiposCalculoManipulacao.Dose)
            .forEach(p => {
                p.volumeDose = data.dose;
            });

        if (data.isTrocaManual) {
            state.manipulacao.trocouDoseManualmente = true;
        }
        store.set(uuid, state);
    },
    SET_ITEM_DATA_NEW(store, { uuid, data }: ManipulacaoContextUUIDNew<UpdateItemType>) {
        const state = store.get(uuid);
        switch (data.type) {
            case ItemTableType.ATIVO:
                state.ativos[data.index] = data.item;
                break;
            case ItemTableType.EMBALAGEM:
                state.embalagens[data.index] = data.item;
                break;
            case ItemTableType.CAPSULA:
                state.capsulas[data.index] = data.item;
                break;
        }
    },
    SET_ATIVO_PRINCIPAL_NEW(store, { uuid, data }: ManipulacaoContextUUIDNew<number>) {
        const state = store.get(uuid);

        state.ativos.forEach(p => {
            p.ativoPrincipal = false;
        });
        state.ativos[data].ativoPrincipal = true;
    },
    SET_FRANQUIA_FILIAL_NEW(
        store,
        { uuid, data }: ManipulacaoContextUUIDNew<{ isFranquiaFilial: boolean; estoqueFranquiaId: number }>,
    ) {
        const state = store.get(uuid);
        state.isFranquiaFilial = data.isFranquiaFilial;
        state.estoqueFranquiaId = data.estoqueFranquiaId;
    },
    SET_KIT_EMBALAGEM_OPTIONS_NEW(store, { uuid, data }: ManipulacaoContextUUIDNew<Object[]>) {
        const state = store.get(uuid);
        state.kitEmbalagensOptions = data;
    },
};

export default mutations;
